import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useDeferredValue, useEffect, useRef, useState, type KeyboardEvent } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";

import {
  SETTLEMENT_TYPES,
  isSettlementB,
  settlementLetter,
  settlementSuggestedIva,
  settlementTotalCents,
  type SettlementAmountField,
} from "@devengo/shared";

import { FilePicker } from "../components/file-picker";
import { Dialog, PeriodPicker } from "../components/period-picker";
import { SearchSelect } from "../components/search-select";
import {
  Button,
  Card,
  ErrorBox,
  Input,
  Notice,
  PageTitle,
  Select,
  numClass,
  tableClass,
  tdClass,
  thClass,
} from "../components/ui";
import { api } from "../lib/api";
import {
  centsDecimal,
  decimalCents,
  decimalToInput,
  formatCents,
  formatCuit,
  formatDate,
  parseUserAmount,
} from "../lib/format";
import type { VoucherView } from "../lib/hooks";
import { parsePeriod, periodParam } from "../lib/session";

/** The settlement as the API returns it. */
export interface SettlementView {
  id: string;
  companyId: string;
  period: { year: number; month: number };
  activity: string;
  issueDate: string;
  typeCode: number;
  pointOfSale: number;
  number: number;
  counterpart: { id: string; docType: number; docNumber: string; name: string };
  amounts: Record<SettlementAmountField | "total", string>;
  imageMimeType: string;
  purchaseVoucherId: string | null;
  saleVoucherId: string | null;
  /** Voided whole, with its compra and venta (plan 024): listed, not added. */
  voided: { at: string; by: string; reason: string } | null;
}

export interface SettlementsSearch {
  periodo?: string;
}

export interface SettlementFormSearch {
  periodo?: string;
  id?: string;
}

const settlementNumber = (s: { typeCode: number; pointOfSale: number; number: number }) =>
  `${settlementLetter(s.typeCode)} ${String(s.pointOfSale).padStart(5, "0")}-${String(s.number).padStart(8, "0")}`;

function useInvalidateSettlements(companyId: string) {
  const client = useQueryClient();
  return async () => {
    await client.invalidateQueries({ queryKey: ["settlements", companyId] });
    await client.invalidateQueries({ queryKey: ["vouchers", companyId] });
    await client.invalidateQueries({ queryKey: ["periods", companyId] });
    await client.invalidateQueries({ queryKey: ["caja", companyId] });
    await client.invalidateQueries({ queryKey: ["company-counterparts", companyId] });
  };
}

/** The settlements of a period: the previous system's «Liquidaciones Mercado: Emitidas». */
export function SettlementsPage({
  companyId,
  search,
}: {
  companyId: string;
  search: SettlementsSearch;
}) {
  const navigate = useNavigate();
  const { year, month } = parsePeriod(search.periodo);
  const periodo = periodParam(year, month);
  const list = useQuery({
    queryKey: ["settlements", companyId, periodo],
    queryFn: () =>
      api.get<SettlementView[]>(`/api/companies/${companyId}/periods/${year}/${month}/settlements`),
  });
  const [voiding, setVoiding] = useState<SettlementView | null>(null);
  const all = list.data ?? [];
  // Totals add only what counts: a voided settlement is listed, not added.
  const rows = all.filter((s) => !s.voided);
  const sum = (pick: (s: SettlementView) => number) => rows.reduce((acc, s) => acc + pick(s), 0);
  const c = (s: SettlementView, key: SettlementAmountField | "total") =>
    decimalCents(s.amounts[key]);
  const deductions = (s: SettlementView) =>
    c(s, "commission105") + c(s, "commission21") + c(s, "unloading105") + c(s, "unloading21");

  return (
    <>
      <PageTitle
        subtitle="Cada liquidación se guarda con la foto del papel y genera su compra y su venta al comitente."
        actions={
          <>
            <PeriodPicker
              value={periodo}
              onChange={(value) =>
                void navigate({
                  to: "/empresas/$companyId/liquidaciones",
                  params: { companyId },
                  search: { periodo: value },
                })
              }
            />
            <Link
              to="/empresas/$companyId/liquidaciones/nueva"
              params={{ companyId }}
              search={{ periodo }}
              className="rounded-md bg-brand-primary px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
            >
              Cargar liquidación
            </Link>
          </>
        }
      >
        Liquidaciones
      </PageTitle>
      <Card>
        <ErrorBox error={list.error} />
        {all.length === 0 && !list.isLoading ? (
          <p className="text-sm text-brand-muted">No hay liquidaciones cargadas en este período.</p>
        ) : (
          <table className={tableClass}>
            <thead>
              <tr>
                <th className={thClass}>Fecha</th>
                <th className={thClass}>Liquidación</th>
                <th className={thClass}>Comitente</th>
                <th className={`${thClass} text-right`}>Venta bruta</th>
                <th className={`${thClass} text-right`}>IVA crédito</th>
                <th className={`${thClass} text-right`}>Comisión y descarga</th>
                <th className={`${thClass} text-right`}>IVA débito</th>
                <th className={`${thClass} text-right`}>Total</th>
                <th className={thClass} />
              </tr>
            </thead>
            <tbody>
              {all.map((s) => (
                <tr
                  key={s.id}
                  className={`hover:bg-brand-tint ${s.voided ? "text-brand-muted line-through" : ""}`}
                >
                  <td className={`${tdClass} tabular`}>{formatDate(s.issueDate)}</td>
                  <td className={`${tdClass} tabular`}>
                    {settlementNumber(s)}
                    {s.voided ? (
                      <div className="text-xs text-red-700 no-underline [text-decoration:none]">
                        Anulada el {formatDate(s.voided.at.slice(0, 10))}: {s.voided.reason}
                      </div>
                    ) : null}
                  </td>
                  <td className={tdClass}>
                    <div>{s.counterpart.name}</div>
                    <div className="tabular text-xs text-brand-muted">
                      {formatCuit(s.counterpart.docNumber)}
                    </div>
                  </td>
                  <td className={`${tdClass} ${numClass}`}>{formatCents(c(s, "grossSale"))}</td>
                  <td className={`${tdClass} ${numClass}`}>{formatCents(c(s, "ivaCredit105"))}</td>
                  <td className={`${tdClass} ${numClass}`}>{formatCents(deductions(s))}</td>
                  <td className={`${tdClass} ${numClass}`}>
                    {formatCents(c(s, "ivaDebit105") + c(s, "ivaDebit21"))}
                  </td>
                  <td className={`${tdClass} ${numClass} font-medium`}>
                    {formatCents(c(s, "total"))}
                  </td>
                  <td className={`${tdClass} whitespace-nowrap text-right [text-decoration:none]`}>
                    <a
                      href={`/api/settlements/${s.id}/image`}
                      target="_blank"
                      rel="noreferrer"
                      className="px-1 text-xs text-brand-primary hover:underline"
                    >
                      Imagen
                    </a>
                    {s.voided ? null : (
                      <Link
                        to="/empresas/$companyId/liquidaciones/nueva"
                        params={{ companyId }}
                        search={{ id: s.id, periodo }}
                        className="px-1 text-xs text-brand-primary hover:underline"
                      >
                        Editar
                      </Link>
                    )}
                    <button
                      type="button"
                      className={`px-1 text-xs hover:underline ${s.voided ? "text-brand-primary" : "text-red-700"}`}
                      onClick={() => setVoiding(s)}
                    >
                      {s.voided ? "Reactivar" : "Anular"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            {rows.length ? (
              <tfoot>
                <tr className="font-semibold">
                  <td className={tdClass} colSpan={3}>
                    {rows.length} liquidaci{rows.length === 1 ? "ón" : "ones"}
                    {all.length > rows.length
                      ? ` · ${all.length - rows.length} anulada${all.length - rows.length === 1 ? "" : "s"}, fuera de los totales`
                      : ""}
                  </td>
                  <td className={`${tdClass} ${numClass}`}>
                    {formatCents(sum((s) => c(s, "grossSale")))}
                  </td>
                  <td className={`${tdClass} ${numClass}`}>
                    {formatCents(sum((s) => c(s, "ivaCredit105")))}
                  </td>
                  <td className={`${tdClass} ${numClass}`}>{formatCents(sum(deductions))}</td>
                  <td className={`${tdClass} ${numClass}`}>
                    {formatCents(sum((s) => c(s, "ivaDebit105") + c(s, "ivaDebit21")))}
                  </td>
                  <td className={`${tdClass} ${numClass}`}>
                    {formatCents(sum((s) => c(s, "total")))}
                  </td>
                  <td className={tdClass} />
                </tr>
              </tfoot>
            ) : null}
          </table>
        )}
      </Card>
      {voiding ? (
        <VoidSettlementDialog settlement={voiding} onClose={() => setVoiding(null)} />
      ) : null}
    </>
  );
}

/** Voids or reactivates a settlement whole: never one half (Martín, 2026-10-22). */
function VoidSettlementDialog({
  settlement,
  onClose,
}: {
  settlement: SettlementView;
  onClose: () => void;
}) {
  const invalidate = useInvalidateSettlements(settlement.companyId);
  const [reason, setReason] = useState("");
  const voiding = !settlement.voided;
  const change = useMutation({
    mutationFn: () =>
      api.post(`/api/settlements/${settlement.id}/${voiding ? "void" : "unvoid"}`, { reason }),
    onSuccess: async () => {
      await invalidate();
      onClose();
    },
  });
  return (
    <Dialog title={voiding ? "Anular liquidación" : "Reactivar liquidación"} onClose={onClose}>
      <div className="flex flex-col gap-3">
        <p className="text-sm">
          {voiding ? "Vas a anular" : "Vas a reactivar"} la liquidación{" "}
          <strong>{settlementNumber(settlement)}</strong> de {settlement.counterpart.name}, con su
          compra y su venta.{" "}
          {voiding
            ? "No se borra: queda tachada, con el motivo, y deja de contar en la Caja IVA y los libros."
            : "Vuelven a contar en la Caja IVA y los libros."}
        </p>
        <label className="flex flex-col gap-1 text-sm">
          Motivo (obligatorio)
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={voiding ? "Ej.: cargada dos veces por error" : "Ej.: se anuló por error"}
          />
        </label>
        <ErrorBox error={change.error} />
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant={voiding ? "danger" : "primary"}
            disabled={change.isPending || reason.trim().length < 5}
            onClick={() => change.mutate()}
          >
            {voiding ? "Anular" : "Reactivar"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ─── Form ─────────────────────────────────────────────────────────────────────────────────────

type AmountKey = SettlementAmountField | "total";

interface SettlementForm {
  activity: string;
  issueDate: string;
  period: string;
  pointOfSale: string;
  number: string;
  typeCode: string;
  docNumber: string;
  name: string;
  amounts: Record<AmountKey, string>;
  /** Editing only: why (it is audited). */
  reason: string;
}

/** The previous system's rows, in its order; the sign says how each one moves the total. */
const AMOUNT_ROWS: { key: AmountKey; label: string }[] = [
  { key: "grossSale", label: "Sub total (venta bruta)" },
  { key: "nonTaxed", label: "No gravado" },
  { key: "commission105", label: "Comisiones IVA 10,5 (−)" },
  { key: "commission21", label: "Comisiones IVA 21 (−)" },
  { key: "unloading105", label: "Descarga IVA 10,5 (−)" },
  { key: "unloading21", label: "Descarga IVA 21 (−)" },
  { key: "ivaCredit105", label: "IVA créd. 10,5% (+)" },
  { key: "ivaDebit21", label: "IVA déb. 21% (−)" },
  { key: "ivaDebit105", label: "IVA déb. 10,5% (−)" },
  { key: "retGanancias", label: "Ret. Ganancias (−)" },
  { key: "retIibb", label: "Ret. IIBB (−)" },
  { key: "retIva", label: "Ret. IVA (−)" },
  { key: "total", label: "TOTAL $" },
];

/** Filled by the screen from their bases, as the previous system does, until typed over. */
const COMPUTED: AmountKey[] = ["ivaCredit105", "ivaDebit105", "ivaDebit21", "total"];

const emptyAmounts = (): Record<AmountKey, string> =>
  Object.fromEntries(AMOUNT_ROWS.map(({ key }) => [key, ""])) as Record<AmountKey, string>;

function defaults(search: SettlementFormSearch): SettlementForm {
  const { year, month } = parsePeriod(search.periodo);
  const period = periodParam(year, month);
  return {
    activity: "Comercio",
    issueDate: `${period}-01`,
    period,
    pointOfSale: "1",
    number: "",
    typeCode: "60",
    docNumber: "",
    name: "",
    amounts: emptyAmounts(),
    reason: "",
  };
}

function fromView(s: SettlementView, period: string): SettlementForm {
  return {
    activity: s.activity,
    issueDate: s.issueDate,
    period,
    pointOfSale: String(s.pointOfSale),
    number: String(s.number),
    typeCode: String(s.typeCode),
    docNumber: s.counterpart.docNumber,
    name: s.counterpart.name,
    amounts: Object.fromEntries(
      AMOUNT_ROWS.map(({ key }) => [key, decimalToInput(s.amounts[key])]),
    ) as Record<AmountKey, string>,
    reason: "",
  };
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    // A label, so the text names the row's first field.
    <label className="grid grid-cols-1 items-center gap-1 sm:grid-cols-[13rem_minmax(0,17rem)] sm:gap-3">
      <span className="text-sm font-medium text-brand-text">{label}</span>
      <div>{children}</div>
    </label>
  );
}

export function SettlementFormPage({
  companyId,
  search,
}: {
  companyId: string;
  search: SettlementFormSearch;
}) {
  const navigate = useNavigate();
  const invalidate = useInvalidateSettlements(companyId);
  const editing = Boolean(search.id);
  const existing = useQuery({
    queryKey: ["settlement", search.id],
    queryFn: () => api.get<SettlementView>(`/api/settlements/${search.id}`),
    enabled: editing,
  });
  const form = useForm<SettlementForm>({ defaultValues: defaults(search) });
  const watched = useWatch({ control: form.control });
  const [image, setImage] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  // Computed fields the user typed over keep what was typed (the paper rules). A ref, not state:
  // the fill effects must see it in the same render the stored values arrive (Codex 016 M4).
  const typed = useRef<Set<AmountKey>>(new Set());
  const [problems, setProblems] = useState<string[]>([]);
  const [saved, setSaved] = useState<{
    settlement: SettlementView;
    purchase?: VoucherView;
    sale?: VoucherView | null;
    warnings: string[];
  } | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  // The period is chosen, as in the previous system's screen; its compra and venta go there. A
  // settlement dated earlier stays in the working period; a later date moves it forward.
  const workingPeriod = useRef(defaults(search).period);

  useEffect(() => {
    if (!existing.data) return;
    // A stored settlement keeps its amounts as saved: nothing is recomputed until edited.
    typed.current = new Set(COMPUTED);
    const stored = fromView(
      existing.data,
      periodParam(existing.data.period.year, existing.data.period.month),
    );
    workingPeriod.current = stored.period;
    form.reset(stored);
    setPreview(`/api/settlements/${existing.data.id}/image`);
  }, [existing.data, form, search.periodo]);

  // Preview of the chosen picture; the object URL is released when replaced.
  useEffect(() => {
    if (!image) return;
    const url = URL.createObjectURL(image);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [image]);

  // Next number for this type and point of sale, while the user has not typed one.
  const typeCode = watched.typeCode ?? "60";
  const pointOfSale = watched.pointOfSale ?? "";
  const nextNumber = useQuery({
    queryKey: ["settlement-next", companyId, typeCode, pointOfSale],
    queryFn: () =>
      api.get<{ number: number }>(
        `/api/companies/${companyId}/settlements/next-number?tipo=${typeCode}&pv=${Number(pointOfSale)}`,
      ),
    enabled: !editing && /^\d+$/.test(pointOfSale) && Number(pointOfSale) > 0,
  });
  useEffect(() => {
    if (!editing && nextNumber.data && !form.getFieldState("number").isDirty) {
      form.setValue("number", String(nextNumber.data.number));
    }
  }, [nextNumber.data, editing, form]);

  // The comitente: the company's suppliers, searched on the server.
  const searchText = useDeferredValue((watched.name ?? "").trim());
  const suppliers = useQuery({
    queryKey: ["company-counterparts", companyId, "proveedor", searchText],
    queryFn: () =>
      api.get<{ id: string; docType: number; docNumber: string; name: string }[]>(
        `/api/companies/${companyId}/counterparts?tipo=proveedor${searchText ? `&q=${encodeURIComponent(searchText)}` : ""}`,
      ),
    placeholderData: (previous) => previous,
  });

  const amounts = { ...emptyAmounts(), ...watched.amounts };
  const centsOf = (key: AmountKey): number | null => {
    const parsed = parseUserAmount(amounts[key] ?? "");
    return parsed === null ? null : decimalCents(parsed);
  };
  const allCents = Object.fromEntries(
    AMOUNT_ROWS.map(({ key }) => [key, centsOf(key) ?? 0]),
  ) as Record<AmountKey, number>;
  const letterB = isSettlementB(Number(typeCode));
  const suggested = settlementSuggestedIva(allCents);
  const computedTotal = settlementTotalCents({
    ...allCents,
    ...(letterB ? { ivaCredit105: 0 } : {}),
  });

  // Fill the computed fields from their bases, unless typed over.
  const fill = (key: AmountKey, cents: number, force = false) => {
    if (!force && typed.current.has(key)) return;
    const value = decimalToInput(centsDecimal(cents));
    if (form.getValues(`amounts.${key}`) !== value) form.setValue(`amounts.${key}`, value);
  };
  useEffect(() => {
    // A B settlement has no IVA crédito, whatever was typed before switching (Codex 016 M4).
    // The type as the form holds it now: after a reset this render's `letterB` is stale
    // (Codex 016 M4 round 3).
    const isB = isSettlementB(Number(form.getValues("typeCode")));
    fill("ivaCredit105", isB ? 0 : suggested.ivaCredit105, isB);
    fill("ivaDebit105", suggested.ivaDebit105);
    fill("ivaDebit21", suggested.ivaDebit21);
  });
  useEffect(() => {
    fill("total", computedTotal);
  });

  const save = useMutation({
    mutationFn: (data: object) => {
      const body = new FormData();
      body.append("data", JSON.stringify(data));
      if (image) body.append("image", image);
      return editing
        ? api.uploadPut<{ settlement: SettlementView; warnings: string[] }>(
            `/api/settlements/${search.id}`,
            body,
          )
        : api.upload<{
            settlement: SettlementView;
            purchase: VoucherView;
            sale: VoucherView | null;
            warnings: string[];
          }>(`/api/companies/${companyId}/settlements`, body);
    },
    onSuccess: async (result) => {
      await invalidate();
      if (editing) {
        void navigate({
          to: "/empresas/$companyId/liquidaciones",
          params: { companyId },
          search: { periodo: form.getValues("period") },
        });
        return;
      }
      setSaved(result);
      const keep = form.getValues();
      form.reset({
        ...defaults(search),
        activity: keep.activity,
        issueDate: keep.issueDate,
        period: keep.period,
        typeCode: keep.typeCode,
        pointOfSale: keep.pointOfSale,
        number: String(result.settlement.number + 1),
      });
      typed.current = new Set();
      setImage(null);
      setPreview(null);
      formRef.current?.querySelector<HTMLInputElement>("[data-first-wrapper] input")?.focus();
    },
  });

  const submit = form.handleSubmit((values) => {
    setSaved(null);
    const found: string[] = [];
    if (!image && !editing) found.push("Falta la imagen de la liquidación: subí la foto o el PDF.");
    const amountsOut: Record<string, string> = {};
    for (const { key, label } of AMOUNT_ROWS) {
      const parsed = parseUserAmount(values.amounts[key]);
      if (parsed === null) {
        found.push(`No se entiende el importe "${values.amounts[key]}" en ${label}.`);
      } else amountsOut[key] = parsed;
    }
    // What is sent is what the total was computed with: a B settlement has no IVA crédito.
    if (isSettlementB(Number(values.typeCode))) amountsOut.ivaCredit105 = "0";
    if (editing && values.reason.trim().length < 5) {
      found.push("Contá en pocas palabras por qué cambiás la liquidación (queda en la auditoría).");
    }
    setProblems(found);
    if (found.length) return;
    save.mutate({
      activity: values.activity,
      issueDate: values.issueDate,
      period: parsePeriod(values.period),
      typeCode: Number(values.typeCode),
      pointOfSale: Number(values.pointOfSale),
      number: Number(values.number),
      counterpart: { docType: 80, docNumber: values.docNumber, name: values.name },
      ...amountsOut,
      ...(editing ? { reason: values.reason } : {}),
    });
  });

  /** Enter goes to the next field and saves on the last one, as in the previous system. */
  const onKeyDown = (e: KeyboardEvent<HTMLFormElement>) => {
    if (e.key !== "Enter" || e.defaultPrevented) return;
    const target = e.target as HTMLElement;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
    if (target.type === "file") return;
    e.preventDefault();
    const fields = [
      ...e.currentTarget.querySelectorAll<HTMLElement>("[data-nav], [data-first-wrapper] input"),
    ].filter((el) => !(el as HTMLInputElement).disabled);
    const next = fields.find(
      (el) => target.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING,
    );
    if (next) next.focus();
    else void submit();
  };

  const lookupComitente = async () => {
    const doc = form.getValues("docNumber").replace(/\D/g, "");
    if (!doc || form.getValues("name")) return;
    const found = await api.get<{ docNumber: string; name: string }[]>(
      `/api/counterparts?q=${doc}`,
    );
    const match = found.find((c) => c.docNumber === doc);
    if (match) form.setValue("name", match.name);
  };

  const isPdf = image
    ? image.type === "application/pdf"
    : existing.data?.imageMimeType === "application/pdf";

  return (
    <>
      <PageTitle
        subtitle="Como en el sistema anterior: Enter pasa al campo siguiente; los IVA y el total se calculan solos y se pueden corregir."
        actions={
          <Link
            to="/empresas/$companyId/liquidaciones"
            params={{ companyId }}
            search={{ periodo: watched.period ?? search.periodo ?? "" }}
            className="text-sm text-brand-primary hover:underline"
          >
            Volver a la lista
          </Link>
        }
      >
        {editing ? "Editar liquidación" : "Cargar liquidación"}
      </PageTitle>

      {saved ? (
        <div className="mb-3 flex flex-col gap-2">
          <Notice tone="success">
            Guardada la liquidación {settlementNumber(saved.settlement)} de{" "}
            {saved.settlement.counterpart.name}: generó la compra
            {saved.sale ? " y la venta" : " (sin comisiones ni descargas, no hay venta)"}. Podés
            cargar la siguiente.
          </Notice>
          {saved.warnings.map((w) => (
            <Notice key={w} tone="warning">
              Revisá: {w}
            </Notice>
          ))}
        </div>
      ) : null}

      <form
        ref={formRef}
        onSubmit={submit}
        onKeyDown={onKeyDown}
        className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,34rem)_1fr]"
      >
        <Card title="Liquidaciones Mercado: Emitidas">
          <div className="flex flex-col gap-2">
            <Row label="Actividad">
              <Input data-nav {...form.register("activity")} />
            </Row>
            <Row label="Fecha">
              <Input
                data-nav
                type="date"
                {...form.register("issueDate", {
                  onChange: (e: { target: { value: string } }) => {
                    if (!e.target.value) return;
                    const month = e.target.value.slice(0, 7);
                    form.setValue(
                      "period",
                      month > workingPeriod.current ? month : workingPeriod.current,
                    );
                  },
                })}
              />
            </Row>
            <Row label="Período">
              <Input
                data-nav
                type="month"
                {...form.register("period", {
                  onChange: (e: { target: { value: string } }) => {
                    workingPeriod.current = e.target.value;
                  },
                })}
              />
            </Row>
            <Row label="Tipo">
              <Select data-nav {...form.register("typeCode")}>
                {SETTLEMENT_TYPES.map((t) => (
                  <option key={t.code} value={t.code}>
                    {t.letter}
                  </option>
                ))}
                {/* A settlement stored with 063/064/059 keeps its code when edited. */}
                {existing.data &&
                !SETTLEMENT_TYPES.some((t) => t.code === existing.data.typeCode) ? (
                  <option value={existing.data.typeCode}>
                    {settlementLetter(existing.data.typeCode)} (
                    {String(existing.data.typeCode).padStart(3, "0")})
                  </option>
                ) : null}
              </Select>
            </Row>
            <Row label="Liquidación Nº (pto. vta. / nro.)">
              <div className="flex gap-2">
                <Input
                  data-nav
                  aria-label="Punto de venta"
                  className="w-24"
                  inputMode="numeric"
                  {...form.register("pointOfSale")}
                />
                <Input
                  data-nav
                  aria-label="Número"
                  inputMode="numeric"
                  {...form.register("number")}
                />
              </div>
            </Row>
            <Row label="Cliente (comitente)">
              <div data-first-wrapper>
                <Controller
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <SearchSelect
                      ariaLabel="Comitente"
                      placeholder="Buscar por nombre o CUIT"
                      value={field.value ?? ""}
                      onChange={field.onChange}
                      options={(suppliers.data ?? []).map((c) => ({
                        id: c.id,
                        label: c.name,
                        detail: `CUIT ${formatCuit(c.docNumber)}`,
                      }))}
                      onPick={(option) => {
                        const found = suppliers.data?.find((c) => c.id === option.id);
                        if (!found) return;
                        form.setValue("docNumber", found.docNumber);
                        field.onChange(found.name);
                      }}
                      emptyText="Nuevo: escribí el nombre y el CUIT."
                    />
                  )}
                />
              </div>
            </Row>
            <Row label="CUIT">
              <Input
                data-nav
                aria-label="CUIT del comitente"
                {...form.register("docNumber", { onBlur: () => void lookupComitente() })}
              />
            </Row>
            {AMOUNT_ROWS.map(({ key, label }) => (
              <Row key={key} label={label}>
                <Input
                  data-nav
                  aria-label={label}
                  className={`text-right tabular ${key === "total" ? "font-semibold" : ""}`}
                  placeholder="0,00"
                  disabled={key === "ivaCredit105" && letterB}
                  {...form.register(`amounts.${key}`, {
                    onChange: () => {
                      if (COMPUTED.includes(key)) typed.current.add(key);
                    },
                  })}
                />
              </Row>
            ))}
            {editing ? (
              <Row label="Motivo del cambio">
                <Input
                  data-nav
                  placeholder="Ej.: la comisión estaba mal tipeada"
                  {...form.register("reason")}
                />
              </Row>
            ) : null}
            {Math.abs(computedTotal - (centsOf("total") ?? 0)) > 10 ? (
              <Notice tone="warning">
                El total cargado no coincide con la cuenta ({formatCents(computedTotal)}): revisá
                los importes contra el papel.
              </Notice>
            ) : null}
          </div>
        </Card>

        <Card title="Imagen de la liquidación">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1 text-sm">
              <span className="font-medium">
                Foto o PDF del papel{" "}
                {editing ? "(dejala vacía para conservar la actual)" : "(obligatoria)"}
              </span>
              <FilePicker
                file={image}
                onChange={setImage}
                accept="image/jpeg,image/png,image/webp,application/pdf"
                label="Imagen de la liquidación"
                hint="o arrastrala acá · foto (JPG, PNG) o PDF"
              />
            </div>
            {preview ? (
              isPdf ? (
                <iframe
                  title="Liquidación"
                  src={preview}
                  className="h-[42rem] w-full rounded border"
                />
              ) : (
                <img
                  src={preview}
                  alt="Liquidación cargada"
                  className="max-h-[48rem] w-full rounded border object-contain"
                />
              )
            ) : (
              <p className="text-sm text-brand-muted">
                Sin imagen todavía. Cada liquidación se guarda con la foto del papel.
              </p>
            )}
          </div>
        </Card>

        <div className="flex flex-col gap-2 lg:col-span-2">
          {problems.map((p) => (
            <Notice key={p} tone="warning">
              {p}
            </Notice>
          ))}
          <ErrorBox error={save.error} />
          <div>
            <Button type="submit" disabled={save.isPending}>
              {editing ? "Guardar cambios" : "Guardar liquidación"}
            </Button>
          </div>
        </div>
      </form>
    </>
  );
}
